import {UserDataEntity} from '@modules/authentication';
import {filter, map, take, tap} from 'rxjs/operators';
import {inject, Injectable, OnDestroy} from '@angular/core'; // Import OnDestroy
import {BehaviorSubject, combineLatest, Observable, ReplaySubject, Subscription} from 'rxjs';
import {DataCollection, DataEntity, OctopusConnectService} from 'octopus-connect';
import {
    Group,
    GroupDataCollection, GroupDataEntity,
    Institution,
    Learner
} from '@modules/groups-management/core/definitions';
import {CommunicationCenterService} from '@modules/communication-center';
import {GroupsManagementService} from '@modules/groups-management/core/services/groups-management.service';
import {ContextualService} from '@modules/groups-management/core/services/contextual.service';
import {Subject} from 'rxjs'; // Corrected import path for Subject
import {of} from 'rxjs/internal/observable/of';
import {GroupManagementConfigurationService} from '@modules/groups-management/core/services/group-management-configuration.service';
import {TypedDataCollectionInterface, TypedDataEntityInterface} from 'shared/models/octopus-connect';
import {takeUntil} from "rxjs/operators"; // Import takeUntil
export interface GroupAttributes {
    label: string,
    type: string,
    archived: false,
    projects: string[],
    level: string,
    color: string,
    schoolyear_term:string,
    parents: string[]
}
@Injectable({
    providedIn: 'root'
})
export class GroupService implements OnDestroy { // Implement OnDestroy
    private groupsList: Group[] = [];
    public onGroupsChanged: BehaviorSubject<Group[]> = new BehaviorSubject([]);
    private destroy$ = new Subject<void>(); // Subject for unsubscribing
    public archiveMode = false;
    private schoolyear = 'all';
    private levels: TypedDataEntityInterface<unknown>[];
    private levelsLoaded = false;
    private educationalLevels: DataEntity[];
    private userData: UserDataEntity;
    private groupsCollection: { [key: number]: GroupDataEntity } = {};
    private groupsSubscription: Subscription = null;
    private learnersList: Learner[] = [];
    private filteredInstitutionList: Array<Institution> = [];

    private dataUpdated = new ReplaySubject<void>();

    private groupsManagement = inject(GroupsManagementService);
    private contextualService = inject(ContextualService);
    private octopusConnect = inject(OctopusConnectService);
    private communicationCenter = inject(CommunicationCenterService);
    private config = inject(GroupManagementConfigurationService);

    constructor() {
        this.communicationCenter
            .getRoom('authentication')
            .getSubject('userData')
            .subscribe((data) => {
                this.userData = data;
                if (data) {
                    this.postAuthentication();
                    this.setupContextual();
                } else {
                    this.postLogout();
                }
            });

        this.communicationCenter
            .getRoom('projects-management')
            .getSubject('setProjectGroups')
            .subscribe((data) => this.updateProjectInGroups(data));

        this.communicationCenter
            .getRoom('groups-management')
            .getSubject('joinGroup')
            .pipe(
                tap((data: { id: string, callback: (string) => Observable<DataEntity> }) => this.joinGroup(data.id)),
                tap((data: { id: string, callback: (string) => Observable<DataEntity> }) => data.callback(this.getClass(data.id)))
            )
            .subscribe();

        this.communicationCenter.getRoom('groups-management')
            .getSubject('leaveGroup')
            .pipe(
                tap((data: { id: string }) => this.leaveGroup(data.id)))
            .subscribe();

        this.communicationCenter
            .getRoom('groups-management')
            .getSubject('getGroups')
            .subscribe(({callbackSubject}: { callbackSubject: Subject<Observable<GroupDataCollection>> }) =>
                callbackSubject.next(this.loadGroups())
            );

        // Listen for the initial groups list emitted by the InitializerService
        this.communicationCenter.getRoom('groups-management')
            .getSubject('groupsList')
            .pipe(takeUntil(this.destroy$))
            .subscribe((initialGroups: Group[]) => {
                if (initialGroups) {
                    this.groupsList = initialGroups;
                    // Emit the received list so components listening to onGroupsChanged get it
                    this.onGroupsChanged.next([...this.groupsList]);
                    // Trigger update for contextual conditions
                    this.dataUpdated.next();
                }
            });
        // Removed listener for 'loadInitialData'

        this.communicationCenter
            .getRoom('groups-management')
            .getSubject('add-group')
            .subscribe(({callbackSubject, group}: { group: GroupAttributes, callbackSubject: Subject<Observable<DataEntity>> }) =>
                callbackSubject.next(this.addGroup(group))
            );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get groups() {
        return this.groupsList.slice();
    }

    public loadGroups() {
        return this.octopusConnect.loadCollection('groups') as Observable<GroupDataCollection>;
    }

    private loadLevels(): Observable<TypedDataCollectionInterface<unknown>> {
        return this.groupsManagement.getCollection('levels')
            .pipe(take(1)) as Observable<TypedDataCollectionInterface<unknown>>;
    }

    private loadEducationalLevels(): Observable<DataCollection> {
        return this.groupsManagement
            .getCollection('educational_level');
    }

    addGroup(group) {
        const obs = this.octopusConnect
            .createEntity('groups', {
                label: group.groupname.trim(),
                type: this.groupsManagement.settings.groupType.toString(),
                archived: false,
                projects: [],
                level: group.level || group.educationalLevel,
                color: group.color,
                schoolyear_term: group.schoolyear_term ? group.schoolyear_term : '',
                parents: this.isInstitutionAssociated(group)
            });
        obs.subscribe(() => this.contextualService.onConditionUpdate());

        return obs as Observable<TypedDataEntityInterface<unknown>>;
    }

    /**
     * Given a list of groups, add a project ID to those groups and remove project ID from groups not listed.
     * @param data Object containing groups ID (groups) and project ID (project)
     */
    updateProjectInGroups(data: { project: string | number, groups: (string|number)[] }): void {
        const projectId = data.project.toString();

        // Remove projectId from groups not listed in data.groups
        this.groupsList
            .filter((group: Group) => group.projects.indexOf(projectId) > -1)
            .forEach((group: Group) => {
                if (data.groups.indexOf(group.id) === -1) {
                    group.projects.splice(group.projects.indexOf(projectId), 1);
                    this.saveGroup(group);
                }
            });

        // Add projectId to groups listed in data.groups
        this.groupsList
            .filter((group: Group) => data.groups.indexOf(group.id) > -1)
            .forEach((group: Group) => {
                if (group.projects.indexOf(projectId) === -1) {
                    group.projects.push(projectId);
                    this.saveGroup(group);
                }
            });
    }

    saveGroup(group: Group) {
        const groupEntity = this.groupsCollection[group.id];

        if (groupEntity) {
            groupEntity.set('label', group.groupname.trim());
            groupEntity.set('archived', group.archived);
            groupEntity.set('metacognitionActive', group.metacognitionActive);
            groupEntity.set('projects', group.projects);
            groupEntity.set('color', group.color);

            // @ts-ignore TODO fix this
            groupEntity.set('schoolyear_term', group.schoolyear_term ? group.schoolyear_term : '');
            if (group.parent) {
                groupEntity.set('parents', group.parent.id);
            }

            // @ts-ignore TODO fix this
            groupEntity.set('level', group.level || group.educationalLevel);
            return groupEntity.save();
        }
    }

    deleteGroup(group): Observable<boolean> {
        const groupEntity = this.groupsCollection[group.id];

        if (groupEntity) {
            const observable = groupEntity.remove();
            observable.subscribe(() => this.contextualService.onConditionUpdate());
            return observable;
        }
    }

    getParents(): { id: string, name: string; field: string; hideCode: boolean; code: string }[] {
        return this.filteredInstitutionList;
    }

    getGroups(): string[] {
        return this.groupsList
            .map((group: Group) => group.groupname);
    }

    public getGroupFromName(groupname: string): Group {
        return this.groupsList.find(group => group.groupname === groupname);
    }

    // plateforme: observable citizencode: array
    getLevels() {
        //ctz-setting
        if (this.config.mustUseAnArray()) {
            return this.levels;
        } else {
            return this.loadLevels().pipe(
                map((collection) => collection.entities),
                tap((levels) => {
                    this.levels = levels as TypedDataEntityInterface<unknown>[]
                    this.levelsLoaded = true;
                })
            );
        }
    }

    public getEducationalLevels(): DataEntity[] {
        return this.educationalLevels;
    }

    public filterArchived(archived: boolean) {
        this.archiveMode = archived;
        if (this.schoolyear !== 'all' && this.schoolyear !== '0') {
            // cumul filter schoolYear and archived
            this.onGroupsChanged.next(this.groupsList.filter((group) => group.archived === archived)
                .filter((group) => group.schoolyear_term && group.schoolyear_term['id'] === this.schoolyear));
        } else {
            this.onGroupsChanged.next(this.groupsList.filter((group) => group.archived === archived));
        }
    }

    /**
     * filter on school year take care of archived flag
     * @param schoolYearFromFilter
     */
    public filterSchoolYear(schoolYearFromFilter?: string) {
        this.schoolyear = schoolYearFromFilter && schoolYearFromFilter !== '' ? schoolYearFromFilter : this.groupsManagement.getCurrentSchoolyearTermId();
        if (this.schoolyear === 'all') {
            this.onGroupsChanged.next(this.groupsList.filter((group) => group.archived === this.archiveMode));
        } else {
            this.onGroupsChanged.next(this.groupsList
                .filter((group) => group.schoolyear_term && group.schoolyear_term['id'] === this.schoolyear)
                .filter((group) => group.archived === this.archiveMode));
        }
    }

    public joinGroup(group: GroupDataEntity | string ) {

        if (typeof group === 'object' && !Array.isArray(group)) {
            if (!this.classExist(group.id) && +group.get('type') === 2) {
                const currentAllIds = [];
                currentAllIds.push(...this.userData.get('groups'));

                for (const entity in this.groupsCollection) {
                    if (currentAllIds.indexOf(entity) !== -1) {
                        currentAllIds.splice(currentAllIds.indexOf(entity), 1);
                    }
                }

                this.groupsList = [];
                this.groupsList.push({
                    id: +group.id,
                    groupname: group.get('label'),
                    color: group.get('color'),
                    learnersIds: [],
                    learners: 0,
                    level: (group.get('level') ? group.get('level') : ''),
                    code: (group.get('code') ? group.get('code') : group.id.toString()),
                    projects: (group.get('projects') ? group.get('projects').slice() : []),
                    archived: group.get('archived'),
                    metacognitionActive: group.get('metacognitionActive'),
                    parent: (group.get('parents') ? group.get('parents')[0] : ''),
                    // @ts-ignore TODO fix this
                    schoolyear_term: (group.get('schoolyear_term') ? group.get('schoolyear_term') : ''),
                    admins: group.get('admins'),
                    locked: group.get('locked')
                });
                this.onGroupsChanged.next(this.groupsList);
                currentAllIds.push(group.id);
                this.userData.set('groups', currentAllIds);
                return this.userData.save();
            }
        } else if (typeof group === 'object' && Array.isArray(group) && group.length > 0) {
            const currentAllIds = [];
            group.forEach(g => {
                if (!this.classExist(g)) {
                    currentAllIds.push(g.toString());
                }
            });

            if (this.userData.get('groups') && this.userData.get('groups').length > 0) {
                // push group that already exist
                currentAllIds.push(...this.userData.get('groups'));
            }

            this.userData.set('groups', currentAllIds);
            return this.userData.save();
        } else if (typeof group === 'string') {
            if (!this.classExist(group)) {
                const currentAllIds = [group];
                this.userData.set('groups', currentAllIds);
                return this.userData.save();
            }
        }

    }

    private leaveGroup(id) {
        if (this.userData.get('groups') && this.userData.get('groups').length > 0) {
            const currentAllIds = [];
            currentAllIds.push(...this.userData.get('groups').filter(g => g !== id));
            this.userData.set('groups', currentAllIds);
            this.userData.save();
        }
    }

    private getClass(id)  {
        const obs = this.octopusConnect.loadEntity('groups', +id.toString());
        obs.subscribe();

        return obs as Observable<TypedDataEntityInterface<unknown>>
    }

    private classExist(id) {
        return id && this.userData.get('groups').indexOf(id.toString()) !== -1 && !!this.groupsList.find((item) => item.id.toString() === id.toString());
    }

    private postLogout() {
        if (this.groupsSubscription) {
            this.groupsSubscription.unsubscribe();
            this.groupsSubscription = null;
        }
    }

    // Restored original postAuthentication logic
    private postAuthentication() {
        if (!this.groupsSubscription) {

            const learnerList$ = this.communicationCenter
                .getRoom('groups-management')
                .getSubject('learnerList');

            const subjectInstitutionListChanged = this.communicationCenter
                .getRoom('groups-management')
                .getSubject<Institution[]>('institutionListFiltered');

            const obsLoad = this.loadGroups();

            this.groupsSubscription = combineLatest([obsLoad, subjectInstitutionListChanged]).subscribe(([groupCollection, institutions]) => {
                this.groupsList = [];
                this.formatAndCacheGroups(groupCollection.entities); // Use renamed method
                this.filteredInstitutionList = institutions;
                this.filterArchived(this.archiveMode);
            });


            obsLoad.pipe(take(1)).subscribe((data: DataCollection) => {

                subjectInstitutionListChanged.subscribe((institutionListFiltered: Array<{ id: string, name: string; field: string; hideCode: boolean; code: string }>) => {
                    this.filteredInstitutionList = institutionListFiltered;
                });

                learnerList$.pipe(filter(learnerList => !!learnerList)).subscribe((learnersList: Learner[]) => {
                    this.learnersList = learnersList;
                    this.groupsList.forEach((group: Group) => {
                        const learners = this.learnersList
                            .filter((learner: Learner) => learner.groups && learner.groups.indexOf(group.groupname) > -1)
                            .map((learner: Learner) => learner.id);

                        group.learners = learners.length;

                        group.learnersIds = this.learnersList
                            .filter((learner: Learner) => learner.groups && learner.groups.indexOf(group.groupname) > -1)
                            .map((learner: Learner) => +learner.id);
                    });

                    this.dataUpdated.next();
                });
            });

            subjectInstitutionListChanged.subscribe((institutionListFiltered) => {
                this.filteredInstitutionList = institutionListFiltered;
            });

            this.loadLevels().pipe(
                map((collection) => collection.entities),
                tap((levels) => this.levels = levels)
            ).subscribe();

            this.loadEducationalLevels().pipe(
                map((collection: DataCollection) => collection.entities),
                tap((educationalLevels: DataEntity[]) => this.educationalLevels = educationalLevels)
            ).subscribe();
        }
        if (this.groupsManagement.settings.filterGroupListingsByCurrentYearByDefault) {
            this.filterSchoolYear();
        }
    }

    // Renamed from formatEntityInstitution and made public for potential reuse if needed,
    // but primarily called by loadAndEmitInitialGroups now.
    private formatAndCacheGroups(entities: GroupDataEntity[]): Group[] {
        const groupsList: Group[] = []; // Use local variable to build the list
        this.groupsCollection = {}; // Reset local cache

        for (const entity of entities) {
            if ((parseInt(entity.get('type') ? entity.get('type').toString() : '', 10)) === this.groupsManagement.settings.groupType) {
                const group: Group = {
                    id: parseInt(entity.id.toString(), 10),
                    groupname: entity.get('label'),
                    color: entity.get('color'),
                    learnersIds: [], // Populated later by combineLatest subscription
                    learners: 0,
                    level: (entity.get('level') ? entity.get('level') : ''),
                    code: (entity.get('code') ? entity.get('code') : entity.id).toString(),
                    projects: (entity.get('projects') ? entity.get('projects').slice() : []),
                    archived: entity.get('archived'),
                    metacognitionActive: entity.get('metacognitionActive'),
                    parent: (entity.get('parents') ? entity.get('parents')[0] : ''),
                    schoolyear_term: (entity.get('schoolyear_term') ? entity.get('schoolyear_term') : null),
                    admins: entity.get('admins'),
                    locked: entity.get('locked')
                };

                groupsList.push(group);
                this.groupsCollection[entity.id] = entity; // Keep caching the entity if needed
            }
        }
        // Emit the list from here as it was originally done
        this.communicationCenter
             .getRoom('groups-management')
             .next('groupsList', groupsList); // Emit formatted list
        return groupsList; // Return the formatted list
    }

    /**
     * Loads initial groups, formats them, updates local state, and emits the list.
     * Triggered by the initializer service.
     */
    // Removed loadAndEmitInitialGroups method

    private isInstitutionAssociated(val: Group) {
        if (val && val.parent) {
            return [val.parent.id];
        }
        if (this.groupsManagement.isGroupMustBeAssociatedWithUsersInstitution()
            && this.filteredInstitutionList && this.filteredInstitutionList.length) {
            return [this.filteredInstitutionList[0].id];
        } else {
            return null;
        }
    }

    private setupContextual() {
        this.contextualService.conditionGroupNoGroup$
            .subscribe((callback) => {
                this.dataUpdated.subscribe(() => callback(this.groupsList.length === 0));
            });

        this.contextualService.conditionGroupNoLearnerInGroup$
            .subscribe((callback) => {
                this.dataUpdated.subscribe(() => callback(this.groupsList.every((group) => group.learners === 0)));
            });
    }
}
